/**
 * Recuerda la última ruta hija bajo `/layout/vault/vault/…` (ingesta) para que,
 * al volver desde otro módulo mediante el menú (ruta genérica → lista de proyectos),
 * se restaure la vista profunda (p. ej. `projects/15/upload`).
 */

export const VAULT_INGEST_LAST_PATH_KEY = 'nebula_vault_ingest_last_child_path';

function readStoredChildPath(): string | null {
  try {
    return typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(VAULT_INGEST_LAST_PATH_KEY)
      : null;
  } catch {
    return null;
  }
}

function writeStoredChildPath(value: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(VAULT_INGEST_LAST_PATH_KEY, value);
    }
  } catch {
    /* storage no disponible o cuota */
  }
}

/**
 * Rutas de primer nivel bajo el shell Vault que NO son el módulo hijo `vault` (ingesta).
 * Si `/layout/vault/<algo>` empieza por uno de estos, no es ingesta (p. ej. `/layout/vault/retention`).
 */
const VAULT_SHELL_NON_INGEST: ReadonlySet<string> = new Set([
  'dashboard',
  'preservation',
  'retention',
  'legal-hold',
  'reports',
  'cloud',
  'notifications',
  'audit',
  'settings',
  'admin',
  'preservation-messages',
  'repository-admin',
]);

function parseIngestChildPathFromUrl(url: string): string | null {
  const clean = url.split('?')[0].split('#')[0].replace(/\/+$/, '');

  const underIngestOutlet = clean.match(/\/layout\/vault\/vault\/(.*)$/i);
  if (underIngestOutlet) {
    return (underIngestOutlet[1] ?? '').replace(/^\/+/, '');
  }

  /**
   * Tras `normalizeRouteStatic` del sidebar, `/layout/vault/vault/` se colapsa a `/layout/vault/`,
   * así que la URL real del router puede ser `.../vault/vault/...` pero la del menú queda `.../vault/projects/...`.
   */
  const collapsed = clean.match(/^\/layout\/vault\/(.+)$/i);
  if (!collapsed) {
    return null;
  }
  const rest = (collapsed[1] ?? '').replace(/^\/+/, '');
  if (!rest) {
    return null;
  }
  const first = rest.split('/')[0]?.toLowerCase() ?? '';
  if (VAULT_SHELL_NON_INGEST.has(first)) {
    return null;
  }
  if (!isSafeVaultIngestChildPath(rest)) {
    return null;
  }
  return rest;
}

function isSafeVaultIngestChildPath(rest: string): boolean {
  if (!rest || rest.includes('..') || rest.includes('\\')) {
    return false;
  }
  try {
    if (decodeURIComponent(rest).includes('..')) {
      return false;
    }
  } catch {
    return false;
  }
  const parts = rest.split('/').filter(Boolean);
  if (parts.length === 0) {
    return false;
  }
  const safeSegment = (s: string): boolean =>
    /^[a-z0-9-]+$/i.test(s) || /^\d+$/.test(s);
  if (!parts.every(safeSegment)) {
    return false;
  }
  const [a, b, c] = parts;
  if (a === 'projects' && parts.length === 1) {
    return true;
  }
  if (a === 'projects' && /^\d+$/.test(b ?? '') && parts.length === 2) {
    return true;
  }
  if (
    a === 'projects' &&
    /^\d+$/.test(b ?? '') &&
    c === 'upload' &&
    parts.length === 3
  ) {
    return true;
  }
  if (a === 'upload' && parts.length === 1) {
    return true;
  }
  if (a === 'files' && parts.length === 1) {
    return true;
  }
  if (a === 'files' && /^\d+$/.test(b ?? '') && parts.length === 2) {
    return true;
  }
  return false;
}

function isGenericVaultIngestChildPath(rest: string): boolean {
  return rest === 'projects' || rest === 'upload' || rest === 'files';
}

/** Llamar desde `NavigationEnd` cuando la URL final esté bajo ingest. */
export function recordVaultIngestPathFromRouterUrl(url: string): void {
  const child = parseIngestChildPathFromUrl(url);
  if (child === null || !child) {
    return;
  }
  if (!isSafeVaultIngestChildPath(child)) {
    return;
  }
  const currentStored = readStoredChildPath();
  // Regla de estabilidad: no permitir que una navegación genérica a "projects"
  // pise una ruta más específica ya guardada (upload, projects/:id/upload, etc.).
  if (
    child === 'projects' &&
    currentStored &&
    currentStored !== 'projects' &&
    isSafeVaultIngestChildPath(currentStored)
  ) {
    return;
  }
  writeStoredChildPath(child);
}

/**
 * Si el menú apunta solo a la raíz de ingesta o a la lista de proyectos,
 * sustituye por la última ruta hija guardada (si es válida y no es solo `projects`).
 */
/** Rutas “genéricas” del menú que deben sustituirse por la última vista de ingesta guardada. */
function isGenericVaultIngestMenuPath(rl: string): boolean {
  // En este producto el menú de "Ingesta" suele apuntar a `.../projects` como entrada base.
  // Lo tratamos como genérico para poder rehidratar la última ruta profunda guardada.
  return (
    rl === '/layout/vault/vault' ||
    rl === '/layout/vault' ||
    rl === '/layout/vault/vault/projects' ||
    rl === '/layout/vault/projects'
  );
}

export function mergeVaultIngestMenuRoute(normalizedRoute: string): string {
  const r = normalizedRoute.split('?')[0].replace(/\/+$/, '');
  const rl = r.toLowerCase();

  if (!isGenericVaultIngestMenuPath(rl)) {
    return normalizedRoute;
  }

  const saved = readStoredChildPath();
  if (!saved || saved === 'projects') {
    return normalizedRoute;
  }
  if (!isSafeVaultIngestChildPath(saved)) {
    return normalizedRoute;
  }
  return `/layout/vault/vault/${saved}`;
}
